import { createFileRoute } from "@tanstack/react-router"

type RouteSearch = { page: number }

export const Route = createFileRoute("/app/duties/leave-requests/")({
  component: ReviewLeaveRequestsPage,
  beforeLoad: (context) => {
    return {
      getTitle: () => context.context.ts.absences.t("LeaveRequests_PageTitle"),
    }
  },
  validateSearch: (search: Record<string, unknown>): RouteSearch => {
    return { page: Number(search.page || 1) }
  },
})

import {
  SelectProps,
  Select,
  Center,
  Button,
  Stack,
  Text,
  Group,
  Box,
} from "@mantine/core"
import { useElementSize } from "@mantine/hooks"
import dayjs from "dayjs"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { AbsentApprovalStatusBadge } from "../../../../components/absences/absent-approval-status-badge"
import { AbsentApprovalStatusIcon } from "../../../../components/absences/absent-approval-status-icon"
import { getFormattedPeriod } from "../../../../components/absences/getFormattedPeriod"
import {
  ResponsiveFilterGroup,
  useResponsiveFilterQueryOptions,
} from "../../-components/hideable-filter-group"
import { Page } from "../../../../-components/Page"
import {
  GetMyApprovalsRequest,
  MyApprovalListItem,
  MyDecisionFilter,
  StatusFilter,
  SortDirection as Sort,
} from "../../../../@client/Theasoft_Absences_MyApprovals"
import { ErrorAlert } from "../../../../blocks/errors/error-alert"
import { RenderShiftType } from "../../../../blocks/shift-types/render-shift-type"
import { useBreakpoints } from "../../../../utils/use-breakpoints"
import { useTranslations } from "../../../../utils/use-t"
import { useIsSmallScreen } from "../../../../utils/useIsSmallScreen"
import { InfinitePersonSingleSelect } from "../../../../-components/SelectFromQuery/InfinitePersonSingleSelect"
import { PersonName } from "@components/PersonName"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { get } from "@/typed-client/get"
import {
  InfiniteScrollingTable,
  TheasoftColumnDef,
} from "@/routes/-components/custom-tanstack-table"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"

type EnumSelectProps<T> = {
  value: T | null
  data: { value: T; label: string }[]
  onChange: (du: T | null) => void
} & Omit<SelectProps, "data" | "value" | "onChange">

const EnumSelect = <T extends string>({
  value,
  onChange,
  data,
  ...rest
}: EnumSelectProps<T>) => (
  <Select
    {...rest}
    value={value}
    data={data}
    onChange={(v) => {
      if (v !== null) {
        onChange(v as T)
      } else {
        onChange(null)
      }
    }}
  />
)

export function ReviewLeaveRequestsPage() {
  const [filter, setFilter] = useUserLocalStorage<
    Omit<GetMyApprovalsRequest, "pageParam" | "pageSize" | "personId">
  >({
    key: "leave-requests-filter",
    defaultValue: {
      myDecision: "All",
      statusFilter: "All",
      sort: "RequestedAtDesc",
    },
  })
  const [personId, setPersonId] = React.useState<number | null>(null)
  const [filtersOpened, setFiltersOpened] = useUserLocalStorage({
    key: "leave-requests-filters-opened",
    defaultValue: false,
  })
  const queryOptions = useResponsiveFilterQueryOptions(filtersOpened)

  const queryResult = useDefaultInfiniteQuery(
    (input: GetMyApprovalsRequest) => get("/api/Absence/GetMyApprovals", input),
    (pageRequest) => ({
      ...pageRequest,
      ...filter,
      personId,
    }),
    queryOptions
  )

  const isSmallScreen = useIsSmallScreen()
  const navigate = Route.useNavigate()
  const ts = useTranslations()
  const { t } = useTranslation("app_server")
  const layout = useBreakpoints()
  const { ref: contentRef, width: contentWidth } = useElementSize()
  const statusDesktopWidth = 110
  const showNoteColumn = contentWidth >= 1000
  const showRequestedAtColumn = contentWidth >= 800

  const columns = useMemo<TheasoftColumnDef<MyApprovalListItem>[]>(
    () => [
      {
        accessorKey: "result",
        header: t("Status"),
        size: isSmallScreen ? 70 : 120,
        cell: ({
          row: {
            original: { result, myStatus },
          },
        }) => {
          const needsReview = myStatus.includes("ActivePending")

          if (needsReview) {
            if (isSmallScreen) {
              return (
                <Center>
                  <Button size="compact-xs" color="pink">
                    {ts.appServer.t("ReviewNow")}
                  </Button>
                </Center>
              )
            }

            return (
              <Button
                size="compact-xs"
                color="pink"
                style={{ width: statusDesktopWidth }}
              >
                {ts.appServer.t("ReviewNow")}
              </Button>
            )
          }

          if (!result) {
            return null
          }

          if (isSmallScreen) {
            return (
              <Center>
                <AbsentApprovalStatusIcon size="sm" status={result} />
              </Center>
            )
          }

          return (
            <AbsentApprovalStatusBadge
              status={result}
              variant="light"
              style={{ width: statusDesktopWidth }}
            />
          )
        },
      },
      {
        accessorKey: "RequestedByAndTimePeriod",
        header: ts.appServer.t("RequestedBy"),
        size: isSmallScreen ? 250 : 300,
        cell: ({
          row: {
            original: { personStatus, period },
          },
        }) => (
          <Stack gap={0}>
            <Group wrap="nowrap" gap="xs">
              <Box
                style={{
                  flex: "0 0 65%",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <PersonName personId={personStatus.personId} />
              </Box>
              <Box style={{ flex: "0 0 35%", minWidth: 0 }}>
                <RenderShiftType id={personStatus.shiftTypeId}>
                  {(v) => (
                    <Text ta="left" truncate="end" size="sm">
                      {v.name}
                    </Text>
                  )}
                </RenderShiftType>
              </Box>
            </Group>
            <Text c="dimmed" size="sm">
              {period ? getFormattedPeriod(period, "dd. DD.MM.YYYY") : ""}
            </Text>
          </Stack>
        ),
      },
      ...(showRequestedAtColumn
        ? [
            {
              accessorKey: "RequestedAt",
              header: ts.appServer.t("RequestedAt"),
              size: 120,
              cell: ({ row }) => (
                <Text c="dimmed">
                  {dayjs(row.original.createdAt).format("L")}
                </Text>
              ),
            } as TheasoftColumnDef<MyApprovalListItem>,
          ]
        : []),
      ...(showNoteColumn
        ? [
            {
              accessorKey: "Note",
              header: ts.appServer.t("Note"),
              cell: ({ row }) => (
                <Text
                  c="orange"
                  truncate="end"
                  style={{ display: "block", width: "100%" }}
                  title={row.original.personStatus.requesterNote ?? ""}
                >
                  {row.original.personStatus.requesterNote}
                </Text>
              ),
            } as TheasoftColumnDef<MyApprovalListItem>,
          ]
        : []),
    ],
    [layout, showNoteColumn, showRequestedAtColumn, t, ts]
  )

  return (
    <Page
      buttonGroupProps={{
        children: (
          <ResponsiveFilterGroup.Button
            filters={{
              requestedBy: personId,
              myDecision: filter.myDecision !== "All",
              status: filter.statusFilter !== "All",
            }}
            visible={filtersOpened}
            onChange={() => setFiltersOpened((prev) => !prev)}
          />
        ),
      }}
    >
      <Stack h="100%" ref={contentRef}>
        <ErrorAlert error={queryResult.error} />
        <ResponsiveFilterGroup
          visible={filtersOpened}
          setVisible={setFiltersOpened}
          filters={{
            requestedBy: personId,
            myDecision: filter.myDecision !== "All",
            status: filter.statusFilter !== "All",
          }}
          onReset={() => {
            setPersonId(null)
            setFilter((current) => ({
              ...current,
              myDecision: "All",
              statusFilter: "All",
            }))
          }}
        >
          <InfinitePersonSingleSelect
            label={ts.appServer.t("RequestedBy")}
            value={personId ? personId.toString() : undefined}
            onSelectionChange={(values) =>
              setPersonId(values ? parseInt(values) : null)
            }
            filterOptions={{ personCategoryTypes: ["Person"] }}
          />

          <EnumSelect<MyDecisionFilter>
            label={ts.appServer.t("MyDecision")}
            value={filter.myDecision}
            onChange={(v) => {
              if (v !== null) {
                setFilter((filter) => ({
                  ...filter,
                  myDecision: v,
                }))
              }
            }}
            data={[
              { value: "All", label: ts.absences.t("Filter_All") },
              { value: "Approved", label: ts.absences.t("Filter_Approved") },
              { value: "Pending", label: ts.absences.t("Filter_Pending") },
              {
                value: "PendingAndActiveOrInfo",
                label: ts.absences.t("Filter_Current"),
              },
              { value: "Rejected", label: ts.absences.t("Filter_Rejected") },
            ]}
          />

          <EnumSelect<StatusFilter>
            label="Status"
            data={[
              { value: "All", label: ts.absences.t("Filter_All") },
              { value: "Rejected", label: ts.absences.t("Filter_Rejected") },
              { value: "Approved", label: ts.absences.t("Filter_Approved") },
              { value: "Pending", label: ts.absences.t("Filter_Pending") },
            ]}
            onChange={(v) => {
              if (v !== null) {
                setFilter((filter) => ({ ...filter, statusFilter: v }))
              }
            }}
            value={filter.statusFilter}
          />

          <EnumSelect<Sort>
            label={ts.appServer.t("Sort")}
            data={[
              {
                value: "RequestedAtDesc",
                label: ts.absences.t("Sort_RequestedAtDesc"),
              },
              {
                value: "RequestedAtAsc",
                label: ts.absences.t("Sort_RequestedAtAsc"),
              },
            ]}
            onChange={(v) => {
              if (v !== null) {
                setFilter((filter) => ({ ...filter, sort: v }))
              }
            }}
            value={filter.sort}
          />
        </ResponsiveFilterGroup>
        <InfiniteScrollingTable
          id="my-leave-requests-table"
          infiniteQueryResult={queryResult}
          columnDefinitions={columns}
          getRowProps={(row) => ({
            onClick: () => {
              navigate({
                to: "/app/duties/leave-requests/$id",
                params: {
                  id: row.original.personStatus.personStatusId.toString(),
                },
              })
            },
            style: { cursor: "pointer" },
          })}
          tableOptions={{
            enableColumnResizing: false,
          }}
          withSettingsMenu={false}
        />
      </Stack>
    </Page>
  )
}
